import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";

import SelectField from "../ui/SelectField";
import InputField from "../ui/InputField";

const FormFirmarServicios = ({ onSigned, onClose }) => {
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [serviciosPendientes, setServiciosPendientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [busqueda, setBusqueda] = useState("");

  const [clienteId, setClienteId] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [areaIds, setAreaIds] = useState([]);
  const [personaAutoriza, setPersonaAutoriza] = useState("");
  const [firma, setFirma] = useState("");

  const sigCanvas = useRef(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  /* ===============================
     Clientes
     =============================== */
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const res = await axios.get(`${API}/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClientes(res.data);
      } catch {
        toast.error("Error al cargar clientes");
      }
    };
    fetchClientes();
  }, [API, token]);

  /* ===============================
     Áreas por cliente
     =============================== */
  useEffect(() => {
    if (!clienteId) {
      setSedes([]);
      setAreas([]);
      setServiciosPendientes([]);
      setSedeId("");
      setAreaIds([]);
      return;
    }
    const fetchPendientes = async () => {
      try {
        const res = await axios.get(
          `${API}/cronograma/completados?cliente=${clienteId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const pendientes = res.data || [];
        setServiciosPendientes(pendientes);

        const sedesUnicas = [
          ...new Map(
            pendientes
              .map((s) => s.equipo?.sede)
              .filter((s) => s?._id)
              .map((s) => [s._id, s]),
          ).values(),
        ];
        setSedes(sedesUnicas);
      } catch {
        toast.error("Error al cargar sedes con servicios pendientes por firma");
      }
    };
    fetchPendientes();
  }, [clienteId, API, token]);

  useEffect(() => {
    setAreaIds([]);
    if (!sedeId) {
      setAreas([]);
      return;
    }

    const areasUnicas = [
      ...new Map(
        serviciosPendientes
          .filter((s) => (s.equipo?.sede?._id || s.equipo?.sede) === sedeId)
          .map((s) => s.equipo?.area)
          .filter((a) => a?._id)
          .map((a) => [a._id, a]),
      ).values(),
    ];
    setAreas(areasUnicas);
  }, [sedeId, serviciosPendientes]);

  /* ===============================
     Servicios completados
     =============================== */
  const fetchServicios = async () => {
    if (!clienteId || !sedeId || !areaIds.length) return;
    try {
      setIsSearching(true);
      const areaParam = areaIds.join(",");
      const sedeParam = sedeId ? `&sede=${encodeURIComponent(sedeId)}` : "";
      const res = await axios.get(
        `${API}/cronograma/completados?cliente=${clienteId}&area=${areaParam}${sedeParam}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setServicios(res.data);
      setSeleccionados([]);
      setIsSearching(false);
    } catch {
      setIsSearching(false);
      toast.error("Error al cargar servicios completados");
    }
  };

  const serviciosFiltrados = servicios.filter((s) => {
    if (!busqueda.trim()) return true;
    const term = busqueda.trim().toLowerCase();
    const campos = [
      s.equipo?.categoria?.name,
      s.equipo?.sede?.name,
      s.equipo?.area?.grupo?.name,
      s.equipo?.area?.name,
      s.equipo?.marca?.name,
      s.equipo?.modelo?.name,
      s.equipo?.serial,
      s.tipo,
      s.fechaProgramada
        ? new Date(s.fechaProgramada).toLocaleDateString("es-CO")
        : null,
    ];
    return campos.some((c) => c?.toString().toLowerCase().includes(term));
  });

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const toggleArea = (id) => {
    setAreaIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  /* ===============================
     Firma
     =============================== */
  const clearFirma = () => {
    if (sigCanvas.current) sigCanvas.current.clear();
    setFirma("");
  };

  const saveFirma = () => {
    const sig = sigCanvas.current;
    if (!sig || sig.isEmpty()) return toast.error("La firma está vacía");
    const dataURL = sig.getCanvas().toDataURL("image/png");
    setFirma(dataURL);
    toast.success("Firma capturada ✅");
  };

  const handleFirmarSeleccionados = async () => {
    setIsLoading(true);

    if (!personaAutoriza || !firma) {
      toast.warning("Debe ingresar el nombre y capturar la firma");
      setIsLoading(false);
      return;
    }

    if (!seleccionados.length) {
      toast.info("Debe seleccionar al menos un servicio");
      setIsLoading(false);
      return;
    }

    try {
      const obtenerUbicacion = () =>
        new Promise((resolve) => {
          if (!navigator.geolocation) return resolve(null);
          navigator.geolocation.getCurrentPosition(
            (pos) =>
              resolve({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
              }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 5000 },
          );
        });

      const obtenerIP = async () => {
        try {
          const res = await fetch("https://api.ipify.org?format=json");
          const data = await res.json();
          return data.ip || null;
        } catch {
          return null;
        }
      };

      const dispositivo = navigator.userAgent || "Desconocido";
      const [ubicacion, ip] = await Promise.all([
        obtenerUbicacion(),
        obtenerIP(),
      ]);

      await Promise.all(
        seleccionados.map((cronogramaId) =>
          axios.put(
            `${API}/gestion/firmar`,
            {
              cronograma_id: cronogramaId,
              personaAutoriza,
              firmaAutorizacion: firma,
              ubicacion,
              ip,
              dispositivo,
              documentoBase64: firma,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );

      toast.success("✅ Firma registrada con metadatos técnicos");
      fetchServicios();
      if (onSigned) onSigned();
      clearFirma();
      setSeleccionados([]);
      setPersonaAutoriza("");
    } catch {
      toast.error("Error al registrar la firma");
    } finally {
      setIsLoading(false);
    }
  };

  /* ===============================
     Resize canvas (sin tocar)
     =============================== */
  useEffect(() => {
    const resize = () => {
      const container = containerRef.current;
      const sig = sigCanvas.current;
      if (!container || !sig) return;

      const canvas = sig.getCanvas();
      const width = container.clientWidth || 600;

      // Detectar móvil
      const isMobile = window.innerWidth < 640;

      // Altura adaptativa
      const baseHeight = isMobile ? 260 : 200;

      const height = Math.max(baseHeight, container.clientHeight || baseHeight);
      const ratio = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      sig.clear();
    };

    resize();

    const ro = new ResizeObserver(resize);
    if (containerRef.current) ro.observe(containerRef.current);

    window.addEventListener("resize", resize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <h2 className="text-xl font-semibold text-blue-600">
            Firmar Servicios Completados
          </h2>

          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-red-500 transition text-2xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <SelectField
              label="Cliente"
              name="cliente"
              value={clienteId}
              options={clientes}
              onChange={(e) => {
                setClienteId(e.target.value);
                setSedeId("");
                setServicios([]);
              }}
              displayFn={(c) => `[${c.nit}] ${c.name}`}
            />

            <SelectField
              label="Sede"
              name="sede"
              value={sedeId}
              options={sedes}
              onChange={(e) => setSedeId(e.target.value)}
              placeholder="Todas las sedes"
              displayFn={(s) => s.name}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Áreas (puede elegir varias)
              </label>
              <div className="max-h-36 overflow-y-auto border rounded-md p-2 space-y-1">
                {areas.map((a) => (
                  <label
                    key={a._id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={areaIds.includes(a._id)}
                      onChange={() => toggleArea(a._id)}
                    />
                    <span>{`${a.grupo?.name} - ${a.name}`}</span>
                  </label>
                ))}
                {!areas.length && (
                  <p className="text-xs text-gray-500">
                    No hay áreas disponibles
                  </p>
                )}
              </div>
            </div>

            <Button
              onClick={fetchServicios}
              disabled={isSearching || !sedeId || !areaIds.length}
            >
              {isSearching ? "Buscando..." : "Buscar"}
            </Button>
          </div>

          {/* Tabla */}
          {servicios.length > 0 && (
            <div className="space-y-3">
              <div className="w-full sm:max-w-xs">
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar en la tabla..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 uppercase text-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-center">✔</th>
                      <th className="px-4 py-2">Equipo</th>
                      <th className="px-4 py-2">Sede</th>
                      <th className="px-4 py-2">Grupo / Área</th>
                      <th className="px-4 py-2">Marca / Modelo</th>
                      <th className="px-4 py-2">Serial</th>
                      <th className="px-4 py-2">Tipo</th>
                      <th className="px-4 py-2">Fecha</th>
                      <th className="px-4 py-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviciosFiltrados.map((s) => (
                      <tr key={s._id} className="border-b hover:bg-gray-50">
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={seleccionados.includes(s._id)}
                            onChange={() => toggleSeleccion(s._id)}
                          />
                        </td>
                        <td className="px-4 py-2">
                          {s.equipo?.categoria?.name || "—"}
                        </td>
                        <td className="px-4 py-2">
                          {s.equipo?.sede?.name || "—"}
                        </td>
                        <td className="px-4 py-2">
                          {s.equipo?.area?.grupo?.name || "—"} /{" "}
                          {s.equipo?.area?.name || "—"}
                        </td>
                        <td className="px-4 py-2">
                          {s.equipo?.marca?.name} - {s.equipo?.modelo?.name}
                        </td>
                        <td className="px-4 py-2">{s.equipo?.serial}</td>
                        <td className="px-4 py-2">{s.tipo}</td>
                        <td className="px-4 py-2">
                          {new Date(s.fechaProgramada).toLocaleDateString(
                            "es-CO",
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              window.open(`/manage/${s._id}`, "_blank")
                            }
                          >
                            Ver servicio
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Firma */}
          {servicios.length > 0 && (
            <div className="border-t pt-6 space-y-4">
              <InputField
                label="Persona que autoriza"
                value={personaAutoriza}
                onChange={(e) => setPersonaAutoriza(e.target.value)}
              />

              <div>
                <label className="block font-medium text-gray-700 mb-2">
                  Firma de autorización
                </label>

                <div
                  ref={containerRef}
                  className="w-full h-[280px] sm:h-[230px]"
                >
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    canvasProps={{
                      className:
                        "w-full h-full border-2 border-gray-300 rounded-xl bg-white touch-none shadow-inner",
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button variant="destructive" onClick={clearFirma}>
                  Limpiar
                </Button>
                <Button onClick={saveFirma}>Capturar firma</Button>
                <Button
                  className="bg-green-600 text-white hover:bg-green-700 ml-auto"
                  onClick={handleFirmarSeleccionados}
                  disabled={isLoading}
                >
                  {isLoading ? "Cargando..." : "Firmar seleccionados"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormFirmarServicios;
